import { useEffect, useRef } from "react";
import { Btn } from "./Btn.js";
import { Icon } from "./Icon.js";
import { mmss } from "../lib/demo.js";

/**
 * Full-screen confirmation gate shown after a capture finishes (camera video / screen recording):
 * nothing is uploaded until 『この内容で送る』, so a bad take can be discarded before any
 * upload / Gemini cost. Keyboard focus is trapped inside (aria-modal); Esc backs out to 撮り直す.
 */
export interface ReviewGateProps {
  /** object URL of the recorded / picked video */
  url: string;
  /** decorative poster (last live frame) — the fallback visual when the blob can't play */
  posterUrl?: string | null;
  /** false once <video> reported it can't play the blob on this device */
  playable: boolean;
  onUnplayable: () => void;
  /** accumulated ACTIVE recording time; omit/0 when unknown (e.g. a picked file) */
  durationMs?: number;
  sending: boolean;
  /** upload progress 0..1 while sending (null = unknown) */
  progress?: number | null;
  /** last send failure — the take is still here, so offer 再送信 instead of leaving the screen */
  error?: string | null;
  onSend: () => void;
  onRetake: () => void;
  onDiscard: () => void;
  /** middle-action label — 撮り直す (camera) / 録画し直す (screen) */
  retakeLabel?: string;
}

export function ReviewGate({
  url,
  posterUrl,
  playable,
  onUnplayable,
  durationMs = 0,
  sending,
  progress = null,
  error = null,
  onSend,
  onRetake,
  onDiscard,
  retakeLabel = "撮り直す",
}: ReviewGateProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);

  // Move focus into the gate when it opens (the opener restores its own focus on close).
  useEffect(() => {
    sendBtnRef.current?.focus();
  }, []);

  /** Keep keyboard focus inside the gate (aria-modal) and let Esc back out to 撮り直す. */
  function onOverlayKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      if (!sending) onRetake();
      return;
    }
    if (e.key !== "Tab") return;
    const root = overlayRef.current;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>("button:not([disabled])"));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="録画内容の確認"
      aria-busy={sending}
      className="review-gate reveal"
      onKeyDown={onOverlayKeyDown}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg,rgba(8,9,12,.78),rgba(8,9,12,.95))",
        padding: "max(18px, env(safe-area-inset-top)) 20px calc(22px + env(safe-area-inset-bottom))",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <div className="kicker" style={{ color: "var(--hi)", letterSpacing: "0.24em" }}>
          撮る · 話す · 送る
        </div>
        <h2 className="display jp" style={{ fontSize: "clamp(24px,5.4vw,38px)", lineHeight: 1.12, margin: "8px 0 0" }}>
          この内容で<span className="hl">送り</span>ますか？
        </h2>
      </div>

      {/* flex (not grid): percentage max-height must resolve against a definite box height,
          or a portrait recording renders at intrinsic size and overlaps the send buttons */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {playable ? (
          <video
            src={url}
            poster={posterUrl ?? undefined}
            controls
            playsInline
            onError={onUnplayable}
            style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 14, background: "#06070a", boxShadow: "0 24px 60px -20px rgba(0,0,0,.8)" }}
          />
        ) : (
          <div style={{ textAlign: "center" }}>
            {posterUrl ? (
              <img src={posterUrl} alt="録画した映像の最後のフレーム" style={{ maxWidth: "100%", maxHeight: "52vh", borderRadius: 14, background: "#06070a" }} />
            ) : null}
            <p className="mono" role="status" style={{ marginTop: 12, fontSize: 12, color: "var(--ink)", opacity: 0.85, letterSpacing: "0.04em" }}>
              この端末ではプレビュー再生ができませんが、録画は保存されています。
            </p>
          </div>
        )}
      </div>

      <p className="mono" style={{ textAlign: "center", margin: "12px 0 16px", fontSize: 12, color: "var(--ink)", opacity: 0.82, letterSpacing: "0.03em" }}>
        {durationMs > 0 ? <>収録 {mmss(durationMs)} · </> : null}送信前なので<span style={{ color: "var(--hi)" }}>破棄してもコストはかかりません</span>
      </p>

      {error ? (
        <p
          role="alert"
          className="jp"
          style={{
            margin: "0 0 12px",
            padding: "10px 14px",
            borderRadius: 12,
            fontSize: 14,
            lineHeight: 1.5,
            color: "var(--ink)",
            border: "1px solid color-mix(in srgb, var(--danger) 70%, transparent)",
            background: "color-mix(in srgb, var(--danger) 18%, transparent)",
          }}
        >
          {error} 録画はこの画面に残っています。
        </p>
      ) : null}

      <div style={{ display: "grid", gap: 10 }}>
        <button ref={sendBtnRef} className="btn btn--hi btn--lg btn--block" disabled={sending} onClick={onSend}>
          {sending
            ? progress != null && progress >= 1
              ? "仕上げ中…"
              : progress != null && progress > 0
                ? `送信中… ${Math.round(progress * 100)}%`
                : "送信中…"
            : error
              ? "もう一度送る"
              : "この内容で送る"}
        </button>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Btn lg icon={<Icon name="capture" size={16} />} onClick={onRetake} disabled={sending} aria-label={retakeLabel}>
            {retakeLabel}
          </Btn>
          <Btn
            variant="ghost"
            lg
            icon={<Icon name="close" size={16} />}
            onClick={onDiscard}
            disabled={sending}
            aria-label="破棄して閉じる"
            style={{ borderColor: "color-mix(in srgb, var(--danger) 60%, transparent)", color: "color-mix(in srgb, var(--danger) 82%, white)" }}
          >
            破棄
          </Btn>
        </div>
      </div>
    </div>
  );
}
